import { createSelector } from "reselect"
import { get, reduce, chain, slice, take, maxBy } from "lodash"
import { getFactorCategory, getFactorLabel, getIsAggregatedFactor } from "helpers/factors"
import { getFactorsMetadata } from "selectors/factors"

const getFactors = (state, props) => props.factors
const getFactorsPerformance = (state, props) => props.factorsPerformance
export const getCategory = state => get(state, "modules.performanceTopFactors.category")

function getTakePlus(count, combine, accumulator) {
  return (values) => {
    if (values.length <= 6) {
      return values
    } else {
      const reduced = reduce(slice(values, 5), combine, accumulator)
      return [...take(values, 5), reduced]
    }
  }
}

const getFactorsPerformanceMap = createSelector(
  [getFactorsPerformance],
  (factorsPerformance) => chain(factorsPerformance)
    .get("model.factors")
    .keyBy(factor => factor.id)
    .mapValues(factor => get(factor, "performance[0].percentPriceChangeCumulative"))
    .value(),
)

export const getTopFactors = createSelector(
  [getFactors, getFactorsPerformanceMap, getCategory, getFactorsMetadata],
  (factors, factorsPerformance, category, factorsMetadata) => chain(factors)
    .map((factor, id) => ({
      id,
      pnl: factor.pnl,
      beta: factor.beta,
      category: getFactorCategory(factorsMetadata, id),
      label: getFactorLabel(factorsMetadata, id),
      isAggregated: getIsAggregatedFactor(factorsMetadata, id),
      performance: get(factorsPerformance, id),
    }))
    .filter(factor => !category || category === factor.category)
    .reduce((accumulator, factor) => {
      if (factor.pnl > 0) {
        accumulator.positive.push(factor)
      } else if (factor.pnl < 0) {
        accumulator.negative.push(factor)
      }

      return accumulator
    }, { positive: [], negative: [] })
    .mapValues(factors =>
      chain(factors)
        .sortBy(factor => -Math.abs(factor.pnl))
        .thru(getTakePlus(5, (accumulator, factor) => {
          return { id: "other", pnl: accumulator.pnl + factor.pnl }
        }, { id: "other", pnl: 0 }))
        .value(),
    )
    .value(),
)

export const getMax = createSelector(
  [getTopFactors],
  (topFactors) => {
    if (!topFactors) {
      return 0
    }

    const factors = [
      ...topFactors.positive,
      ...topFactors.negative,
    ]

    if (factors.length > 0) {
      return Math.abs(maxBy(factors, item => Math.abs(item.pnl)).pnl)
    } else {
      return 0
    }
  },
)
